import {
  addP,
  buildingCornersLm,
  convexHull,
  getBuildingHeight,
  scaleVec,
  toDeg,
  toRad,
} from './geometry';
import type { Building, Point } from './types';

// Pure computation only (sun position / shadow polygon math). The canvas
// drawing (drawAllShadows) and its sunPositionCache live with the rest of
// the rendering layer — see the migration plan.

export const MIN_SUN_ALTITUDE_DEG = 3;

export const HOUR_COLORS: Record<number, string> = {
  6: '#364fc7',
  7: '#1971c2',
  8: '#1c7ed6',
  9: '#0c8599',
  10: '#12b886',
  11: '#37b24d',
  12: '#f59f00',
  13: '#f76707',
  14: '#fd7e14',
  15: '#e8590c',
  16: '#e64980',
  17: '#c2255c',
  18: '#862e9c',
};
export function shadowColorForHour(hour: number): string {
  return HOUR_COLORS[hour] || '#868e96';
}

export function monthDayToDayOfYear(month: number, day: number): number {
  const daysInMonth = [31, 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];
  let n = day;
  for (let m = 1; m < month; m++) n += daysInMonth[m - 1];
  return n;
}
export function solarDeclinationDeg(n: number): number {
  return 23.45 * Math.sin(toRad((360 / 365) * (284 + n)));
}
export function equationOfTimeMin(n: number): number {
  const B = toRad((360 / 365) * (n - 81));
  return 9.87 * Math.sin(2 * B) - 7.53 * Math.cos(B) - 1.5 * Math.sin(B);
}
export function normalizeAngleDeg(a: number): number {
  let d = a % 360;
  if (d < 0) d += 360;
  return d;
}

export interface SunPosition {
  altitudeDeg: number;
  azimuthDeg: number | null;
  visible: boolean;
}

export function computeSunPosition(
  latDeg: number,
  lngDeg: number,
  month: number,
  day: number,
  clockHour: number
): SunPosition {
  const n = monthDayToDayOfYear(month, day);
  const decl = toRad(solarDeclinationDeg(n));
  const phi = toRad(latDeg);
  const timeCorrectionMin = 4 * (lngDeg - 135) + equationOfTimeMin(n);
  const lst = clockHour + timeCorrectionMin / 60;
  const H = toRad(15 * (lst - 12));
  const sinAlt = Math.sin(phi) * Math.sin(decl) + Math.cos(phi) * Math.cos(decl) * Math.cos(H);
  const altDeg = toDeg(Math.asin(Math.max(-1, Math.min(1, sinAlt))));
  if (altDeg <= 0) return { altitudeDeg: altDeg, azimuthDeg: null, visible: false };
  const cosAlt = Math.cos(toRad(altDeg));
  const sinGammaS = (Math.cos(decl) * Math.sin(H)) / cosAlt;
  const cosGammaS = (sinAlt * Math.sin(phi) - Math.sin(decl)) / (cosAlt * Math.cos(phi));
  const gammaS = Math.atan2(sinGammaS, cosGammaS);
  const azimuthDeg = normalizeAngleDeg(180 + toDeg(gammaS));
  return { altitudeDeg: altDeg, azimuthDeg, visible: true };
}

export function computeShadowPolygonLm(b: Building, sunPos: SunPosition | null): Point[] | null {
  if (!sunPos?.visible || sunPos.azimuthDeg === null || sunPos.altitudeDeg < MIN_SUN_ALTITUDE_DEG) {
    return null;
  }
  const H = getBuildingHeight(b);
  const altRad = toRad(sunPos.altitudeDeg);
  const azRad = toRad(sunPos.azimuthDeg);
  const L = H / Math.tan(altRad);
  const shadowDir = { x: -Math.sin(azRad), y: -Math.cos(azRad) };
  const offset = scaleVec(shadowDir, L);
  const corners = buildingCornersLm(b);
  const shifted = corners.map((c) => addP(c, offset));
  return convexHull(corners.concat(shifted));
}

export function hexToRgba(hex: string, alpha: number): string {
  const v = hex.replace('#', '');
  const r = Number.parseInt(v.substring(0, 2), 16);
  const g = Number.parseInt(v.substring(2, 4), 16);
  const bl = Number.parseInt(v.substring(4, 6), 16);
  return `rgba(${r},${g},${bl},${alpha})`;
}
